import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, map, scan } from 'rxjs';

import { SegmentTrackEvent } from '@ninety/platform/models/analytics/segment/segment-track-event.enum';
import { TrashToolSegmentType } from '@ninety/platform/models/analytics/segment/trash-tool-segment-type.enum';
import { FilterServiceActions } from '@ninety/platform/services';
import { LayoutsTodosEvents, selectIsInMeeting, selectIsMyNinetyUrl } from '@ninety/platform/state';
import { trackArchiveViewToggled } from '@ninety/platform/state/app-global/analytics/segment/archive-segment.helpers';
import { selectArchiveTrackingMeeting } from '@ninety/platform/state/app-global/analytics/segment/archive-segment.selectors';
import { SegmentActions } from '@ninety/platform/state/app-global/analytics/segment/segment.actions';
import {
  trackTrashDeleteForever,
  trackTrashRestored,
  trackTrashViewed,
} from '@ninety/platform/state/app-global/analytics/segment/trash-segment.helpers';

import { PersonalTodoActions, PersonalTodoInlineActions } from './personal/personal-todo.actions';
import { TeamTodoActions, TeamTodoInlineActions, TeamTodoTrashActions } from './team/team-todo.actions';
import { TeamTodoSelectors } from './team/team-todo.selectors';

@Injectable()
export class TodosAnalyticsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);

  /** Todo Created — fires when a todo is saved through the universal Create dialog
   *  (UniversalCreateService.create dispatches addMany with the new todo).
   *  addOne triggers are intentionally excluded:
   *  - PersonalTodoActions.addOne has no dispatch sites (dead listener).
   *  - TeamTodoActions.addOne only fires from todos.component.ts on archived-todo re-hydration,
   *    not on creation; including it would emit todo_created on archive toggles. */
  trackTodoCreated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.addMany, PersonalTodoActions.addMany),
      map(({ todo }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.TODO_CREATED,
          params: {
            entityId: todo._id,
            companyId: todo?.companyId ?? '',
            isPersonal: todo.isPersonal ?? false,
            teamId: todo.teamId,
            userId: todo.userId,
            repeat: todo.repeat,
            eventSource: 'regular create',
          },
        })
      )
    )
  );

  /** Todo Created (inline) — fires when a todo is created via the inline-create row. */
  trackTodoCreatedInline$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoInlineActions.createOne, PersonalTodoInlineActions.createOne),
      map(({ todo }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.TODO_CREATED,
          params: {
            entityId: todo._id,
            companyId: todo?.companyId ?? '',
            isPersonal: todo.isPersonal ?? false,
            teamId: todo.teamId,
            userId: todo.userId,
            repeat: todo.repeat,
            eventSource: 'inline create',
          },
        })
      )
    )
  );

  /** Todo Set Completed — fires when a team todo is marked complete.
   *  eventSource comes from the dispatch site ('to-do page' from the team todos page,
   *  'my90' from the My90 widget) and is overridden to 'meeting' when the user is on
   *  a /meeting/<id>... route. Personal-todo completion is handled by the sibling
   *  trackPersonalTodoSetCompleted$ effect below.
   *  Filters on todo.completed === true so un-checking a previously-completed team
   *  todo doesn't get counted as a completion event — both directions dispatch
   *  setCompleted via the facade / My90 widget. */
  trackTodoSetCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.setCompleted),
      filter(({ todo }) => todo.completed === true),
      concatLatestFrom(() => this.store.select(selectIsInMeeting)),
      map(([{ id, todo, eventSource }, isInMeeting]) =>
        SegmentActions.track({
          event: SegmentTrackEvent.TODO_SET_COMPLETED,
          params: {
            entityId: id,
            companyId: todo?.companyId ?? '',
            isPersonal: todo.isPersonal ?? false,
            teamId: todo.teamId,
            userId: todo.userId,
            eventSource: isInMeeting && eventSource === 'to-do page' ? 'meeting' : eventSource,
          },
        })
      )
    )
  );

  /** Todo Set Completed (personal) — fires when a personal todo's completion is toggled.
   *  Listens to PersonalTodoActions.toggleCompleted, the post-API-success signal already
   *  dispatched from both inline (personal-todo.effects.ts:314) and detail-panel
   *  (personal-todo.effects.ts:282) flows. Different trigger surface than the team flow
   *  (team fires on intent, personal on success) because the personal facade only
   *  dispatches PersonalTodoActions.updateInline — there's no team-style setCompleted
   *  dispatch site. eventSource is derived from route since the action doesn't carry it. */
  trackPersonalTodoSetCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PersonalTodoActions.toggleCompleted),
      filter(({ isCompleted }) => isCompleted),
      concatLatestFrom(() => [this.store.select(selectIsInMeeting), this.store.select(selectIsMyNinetyUrl)]),
      map(([{ todo }, isInMeeting, isMyNinety]) => {
        const eventSource = isInMeeting ? 'meeting' : isMyNinety ? 'my90' : 'to-do page';
        return SegmentActions.track({
          event: SegmentTrackEvent.TODO_SET_COMPLETED,
          params: {
            entityId: todo._id,
            companyId: todo?.companyId ?? '',
            isPersonal: todo.isPersonal ?? true,
            teamId: todo.teamId,
            userId: todo.userId,
            eventSource,
          },
        });
      })
    )
  );

  /** Todo Deleted (single) — fires on the delete intent action (post-confirmation dialog).
   *  Intent rather than success because the success action only carries an id and would
   *  lose the schema fields (companyId, teamId, userId) we need on the payload. The
   *  confirmation dialog gates the intent, so fire-on-intent is close to fire-on-action. */
  trackTodoDeleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.delete, PersonalTodoActions.delete),
      map(({ todo }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.TODO_DELETED,
          params: {
            entityId: todo._id,
            companyId: todo?.companyId ?? '',
            isPersonal: todo.isPersonal ?? false,
            teamId: todo.teamId,
            userId: todo.userId,
            seriesId: todo.seriesId ?? null,
            isSeriesDelete: false,
          },
        })
      )
    )
  );

  /** Todo Deleted (series) — fires on the deleteSeries intent action. Emits the same event
   *  as single delete with isSeriesDelete=true so BI can split single vs series deletions. */
  trackTodoSeriesDeleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.deleteSeries, PersonalTodoActions.deleteSeries),
      map(({ todo }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.TODO_DELETED,
          params: {
            entityId: todo._id,
            companyId: todo?.companyId ?? '',
            isPersonal: todo.isPersonal ?? false,
            teamId: todo.teamId,
            userId: todo.userId,
            seriesId: todo.seriesId ?? null,
            isSeriesDelete: true,
          },
        })
      )
    )
  );

  /** Todo Due Date Changed — fires on updateDueDateSuccess, which carries the full persisted todo.
   *  SURFACE-SPECIFIC: the only path that dispatches updateDueDate is the **team todos-page table**
   *  inline date picker (todos.component.html → TeamTodoFacade.onDateChange). Due-date edits from
   *  OTHER surfaces do NOT hit this event — they surface as todo_updated instead:
   *    - the detail card date picker (team AND personal) goes through TodoCardStore.onDateChange →
   *      the generic `update` action → updateSuccess → todo_updated;
   *    - personal todos have no dedicated updateDueDate action at all (PersonalTodoActions.update-
   *      DueDateSuccess exists via the shared action group but is never dispatched).
   *  So this is the clean signal for table-inline team due-date edits; comprehensive due-date
   *  coverage across all surfaces is a follow-up that arrives with changedFields on todo_updated
   *  (the diff isn't available on the success actions to filter on here). Success-gated: a failed
   *  save dispatches updateFailure and records nothing. */
  trackTodoDueDateChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.updateDueDateSuccess),
      map(({ todo }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.TODO_DUE_DATE_CHANGED,
          params: {
            entityId: todo._id,
            companyId: todo?.companyId ?? '',
            isPersonal: todo.isPersonal ?? false,
            teamId: todo.teamId,
            userId: todo.userId,
            dueDate: todo.dueDate,
          },
        })
      )
    )
  );

  /** Todo Updated — generic edit (title, description, owner, repeat, etc.) on the update-success
   *  actions for both team + personal, which carry the full persisted todo. Owner changes go
   *  through a dedicated updateUser → updateUserSuccess action (todo-card.store.ts owner picker)
   *  whose payload is `{ todo }` with no completeStatusChange, so it is included and always passes
   *  the completion filter. Due-date edits surface here too EXCEPT the team todos-page table inline
   *  picker, which is the one surface routed through updateDueDate → todo_due_date_changed; the
   *  detail-card date picker (team + personal) and all personal due-date edits go through the
   *  generic `update`/`updateInline` actions and so land here. Success-gated by construction.
   *
   *  Completion-touching edits are excluded via completeStatusChange so this won't double-count
   *  against todo_set_completed. NOTE: completeStatusChange is a *presence* check at the dispatch
   *  site (action.todo.hasOwnProperty('completed')), not a true change check — it holds only
   *  because current dispatch sites include `completed` solely when toggling. A future "save full
   *  snapshot" dispatch that always carried `completed` would suppress todo_updated; revisit if
   *  such a path is added.
   *
   *  Known over-capture: a comment delete dispatches updateSuccess({ completeStatusChange: false })
   *  (team-todo.effects.ts), so it currently surfaces as a generic todo_updated. Without
   *  changedFields there's no discriminator to split it out — BI question logged in the followups.
   *
   *  changedFields is intentionally omitted for now: the success actions carry the full todo but
   *  not the change diff (only the intent's Partial<Todo> does), and the todos store exposes no
   *  by-id selector to safely pair intent→success under concurrent edits. Tracked as a follow-up. */
  trackTodoUpdated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        TeamTodoActions.updateSuccess,
        TeamTodoActions.updateInlineSuccess,
        TeamTodoActions.updateUserSuccess,
        PersonalTodoActions.updateSuccess,
        PersonalTodoActions.updateInlineSuccess,
        PersonalTodoActions.updateUserSuccess
      ),
      // updateUserSuccess has no completeStatusChange (owner change never toggles completion), so
      // the `in` guard lets it through; the other actions are filtered on the flag.
      filter(action => !('completeStatusChange' in action) || !action.completeStatusChange),
      map(({ todo }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.TODO_UPDATED,
          params: {
            entityId: todo._id,
            companyId: todo?.companyId ?? '',
            isPersonal: todo.isPersonal ?? false,
            teamId: todo.teamId,
            userId: todo.userId,
          },
        })
      )
    )
  );

  /** Only on closed → open; redundant showTrashItems must not double-fire View Trash. */
  trackTrashViewed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LayoutsTodosEvents.showTrashItems, LayoutsTodosEvents.hideTrashItems),
      scan(
        (acc, action) => {
          const show = action.type === LayoutsTodosEvents.showTrashItems.type;
          const shouldTrack = !acc.previous && show;
          return { previous: show, shouldTrack };
        },
        { previous: false, shouldTrack: false }
      ),
      filter(({ shouldTrack }) => shouldTrack),
      map(() => trackTrashViewed(TrashToolSegmentType.ToDos))
    )
  );

  trackTrashRestored$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoTrashActions.restoreTodoSuccess),
      map(({ todoId }) => trackTrashRestored(TrashToolSegmentType.ToDos, todoId))
    )
  );

  trackTrashDeleteForever$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoTrashActions.permanentDeleteTodoSuccess),
      map(({ todoId }) => trackTrashDeleteForever(TrashToolSegmentType.ToDos, todoId))
    )
  );

  trackArchiveToggleClicked$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterServiceActions.archiveToggleClicked),
      concatLatestFrom(() => [
        this.store.select(TeamTodoSelectors.selectIsTodosUrl),
        this.store.select(selectArchiveTrackingMeeting),
      ]),
      filter(([, isInTodos]) => isInTodos),
      map(([{ showArchived }, , meeting]) =>
        trackArchiveViewToggled({
          itemType: 'todo',
          control: 'toggle',
          isArchived: showArchived,
          pageSource: 'to-do page',
          meeting,
        })
      )
    )
  );
}
